"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

import { cn } from "@/lib/utils";

type Status = "idle" | "loading" | "success" | "error";

export function FooterV2() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || status === "loading") return;
    setStatus("loading");
    setErrorMessage(null);
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "landing_footer" }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setErrorMessage(body.error ?? "Something went wrong.");
        setStatus("error");
        return;
      }
      setStatus("success");
      setEmail("");
    } catch {
      setErrorMessage("Network error. Please try again.");
      setStatus("error");
    }
  }

  return (
    <footer className="bg-[#0B1120] px-6 pb-10 pt-16">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-12 border-b border-slate-800/60 pb-12 lg:grid-cols-[1fr_auto] lg:gap-20">
          <div className="max-w-md">
            <p className="font-heading text-[15px] font-semibold tracking-tight text-white">
              Occasional product updates · zero spam
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-slate-400">
              Drop your email if you want to hear when we ship new design
              templates, section variants, or pricing tweaks. About one email a
              month, no marketing fluff.
            </p>
            <form
              onSubmit={submit}
              className="mt-5 flex w-full max-w-sm flex-col gap-2 sm:flex-row"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@domain.com"
                required
                aria-label="Email"
                className={cn(
                  "h-10 flex-1 rounded-full border border-slate-700 bg-slate-900/60 px-4 text-[13px] text-white placeholder:text-slate-500",
                  "focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30",
                )}
              />
              <button
                type="submit"
                disabled={status === "loading" || status === "success"}
                className="inline-flex h-10 shrink-0 items-center justify-center rounded-full bg-white px-5 text-[12.5px] font-semibold text-slate-900 transition-colors hover:bg-slate-100 disabled:opacity-60"
              >
                {status === "loading"
                  ? "Sending…"
                  : status === "success"
                    ? "Subscribed ✓"
                    : "Subscribe"}
              </button>
            </form>
            {status === "error" && errorMessage && (
              <p className="mt-2 text-[12px] text-rose-400">{errorMessage}</p>
            )}
            {status === "success" && (
              <p className="mt-2 text-[12px] text-emerald-400">
                You&apos;re on the list. Talk soon.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-10 sm:gap-16">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Product
              </p>
              <ul className="mt-4 flex flex-col gap-2.5">
                <li>
                  <a
                    href="/#features"
                    className="text-[13px] text-slate-400 hover:text-white"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="/#pricing"
                    className="text-[13px] text-slate-400 hover:text-white"
                  >
                    Pricing
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Legal
              </p>
              <ul className="mt-4 flex flex-col gap-2.5">
                <li>
                  <Link
                    href="/privacy"
                    className="text-[13px] text-slate-400 hover:text-white"
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    href="/terms"
                    className="text-[13px] text-slate-400 hover:text-white"
                  >
                    Terms
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <Link href="/" className="flex items-center">
            <Image
              src="/funnlr_logo.png"
              alt="Funnlr"
              width={200}
              height={48}
              className="h-30 w-auto brightness-0 invert"
            />
          </Link>
          <p className="text-[11px] text-slate-500">
            © 2026 Funnlr. Built in the open.
          </p>
        </div>
      </div>
    </footer>
  );
}
